
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ServiceItem } from '../types';
import { Button } from './Button';
import { agreedMinHours, bookableQuantities, isHourlyUnit } from '../services/serviceUnits';
import { OverContractAddon, overContractAddons } from '../services/additionalServiceCandidates';
import { PlusCircle, X, ArrowRight, Info, Calculator, Clock, Minus, Plus, AlertTriangle } from 'lucide-react';

// Deutsche Anzeige von Mengen und Mindeststunden (2 -> "2", 1.5 -> "1,5") —
// wortgleich zum Abrechnungsdialog, damit dieselbe Vorgabe ueberall gleich dasteht.
const formatQuantity = (value: number): string => value.toLocaleString('de-DE');

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (selectedAddons: Record<string, number>) => void;
    services: ServiceItem[];
}

export const AdditionalServicesModal: React.FC<Props> = ({ isOpen, onClose, onConfirm, services }) => {
    /*
     * ROHTEXT je Position im State, nicht die Zahl — dieselbe Regel wie in den
     * uebrigen Mengenfeldern: das Feld ist type="text", weil ein Zahlenfeld die
     * Zwischenstufe "2." (und, je nach Gebietsschema des Browsers, das getippte
     * "1,5") als LEEREN Wert meldet, waehrend der Text sichtbar stehen bleibt.
     * Der Bildschirm zeigte dann 1,5 und gebucht wurde 0 — besonders nahe
     * liegend, seit der Hinweis darueber "Mindestens 1,5 Std." in deutscher
     * Schreibweise zum Nachtippen einlaedt.
     */
    const [amountTexts, setAmountTexts] = useState<Record<string, string>>({});

    /*
     * Genau die Mengen, die gebucht werden: nur positive Zahlen. Leer heisst
     * hier "nicht erbracht" und ergibt keinen Eintrag; "0" und Unlesbares
     * ("abc", "3..5") ebenfalls nicht — sie buchen keine 0 und loeschen nichts,
     * der getippte Text bleibt im Feld.
     */
    const quantities = useMemo(() => bookableQuantities(amountTexts), [amountTexts]);

    /*
     * Rueckfrage, wenn eine Menge den Vertragsrest ueberschreitet (Vorfall
     * 5634). Gesetzt beim ersten Klick auf „übernehmen"; jede Aenderung an
     * einer Menge nimmt sie zurueck, damit nie eine Bestaetigung fuer Mengen
     * gilt, die so nicht mehr im Dialog stehen.
     */
    const [overWarning, setOverWarning] = useState<OverContractAddon[] | null>(null);

    if (!isOpen) return null;

    const handleValueChange = (id: string, val: string) => {
        setAmountTexts(prev => ({ ...prev, [id]: val }));
        setOverWarning(null);
    };

    const handleConfirm = () => {
        if (overWarning === null) {
            const over = overContractAddons(services, quantities);
            if (over.length > 0) {
                setOverWarning(over);
                return;
            }
        }
        onConfirm(quantities);
    };

    /*
     * Plus/Minus neben dem Feld: auf dem Bau ist die Zusatzleistung fast immer
     * "einmal heute" (eine Baubesprechung, eine Anfahrt) — ein Tipp statt
     * Tastatur aufklappen und tippen. Stunden springen in halben Stunden.
     * Der Schritt geht vom GELESENEN Wert aus und schreibt den Rohtext in
     * deutscher Schreibweise zurueck, damit dieselbe Leseregel gilt wie beim
     * Tippen; 0 leert das Feld ("nicht erbracht"), es wird nie eine 0 gebucht.
     */
    const step = (id: string, isHourly: boolean, direction: 1 | -1) => {
        const current = quantities[id] ?? 0;
        const delta = (isHourly ? 0.5 : 1) * direction;
        const next = Math.max(0, Math.round((current + delta) * 100) / 100);
        handleValueChange(id, next === 0 ? '' : next.toLocaleString('de-DE'));
    };

    const hasAnySelection = Object.keys(quantities).length > 0;
    const selectedCount = Object.keys(quantities).length;

    /*
     * Portal an document.body — wie ParticipantSheet. Das Begehungsmodul
     * traegt animate-scale-in mit fill-mode both: das `transform` bleibt nach
     * der Animation stehen und oeffnet einen eigenen Stacking-Context. Ein
     * z-[200] darin ist gegen die Handy-Navigation (z-[91], Geschwister auf
     * Wurzelebene) wirkungslos — deren Aufklapp-Knopf lag ueber dem Fuss des
     * Dialogs. 100dvh statt inset-0: die Browserleiste auf dem Handy rechnet
     * sonst mit.
     */
    return createPortal((
        /* Auf dem Handy als Bottom-Sheet: von unten, volle Breite, oben rund.
           Auf grossen Schirmen wie bisher mittig. */
        <div className="fixed inset-x-0 top-0 z-[300] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in" style={{ height: '100dvh' }}>
            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[92dvh] sm:max-h-[85vh]">
                <div className="bg-indigo-600 text-white px-4 py-4 sm:p-6 flex justify-between items-center shrink-0 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="bg-white/20 p-2 rounded-xl shrink-0">
                            <PlusCircle className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-base sm:text-lg leading-tight">Zusatzleistungen?</h3>
                            <p className="text-indigo-100 text-xs font-medium">Wurden heute weitere Leistungen erbracht?</p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Schließen" className="hover:bg-white/10 p-2 -mr-2 rounded-full transition-colors shrink-0">
                        <X className="w-6 h-6"/>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50 space-y-4 sm:space-y-6">
                    <div className="bg-blue-50 border border-blue-100 p-3 sm:p-4 rounded-xl flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-800 leading-relaxed">
                            Nur Positionen, die im Projekt als <b>Zusatzleistung</b> hinterlegt sind (z.B. Anfahrt, Baubesprechung, Planaushang). Menge angeben, wenn heute durchgeführt.
                        </p>
                    </div>

                    <div className="grid gap-3">
                        {services.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-dashed">
                                Keine Zusatzleistungen im Projekt-LV gefunden.
                            </div>
                        ) : (
                            services.map(s => {
                                const remaining = Math.max(0, s.quantityContracted - s.quantityUsed);
                                const isHourly = isHourlyUnit(s.unit);
                                // Vereinbarte Mindeststunden nur ANZEIGEN, nie vorbelegen: ein
                                // leeres Feld heisst hier "nicht erbracht", eine Vorbelegung
                                // zoege jede Zusatzleistung stillschweigend in die Rechnung.
                                const minHours = agreedMinHours(s);
                                const selected = quantities[s.id] > 0;

                                return (
                                    <div key={s.id} className={`p-3.5 sm:p-4 bg-white border rounded-2xl transition-all ${selected ? 'border-indigo-500 ring-2 ring-indigo-50' : 'border-slate-200 hover:border-indigo-200'}`}>
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
                                            <div className="flex-1 min-w-0 w-full">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[11px] font-mono font-bold text-slate-400 bg-slate-100 px-1.5 rounded">{s.lvPosition}</span>
                                                    {isHourly ? <Clock className="w-3 h-3 text-indigo-400" /> : <Calculator className="w-3 h-3 text-indigo-400" />}
                                                </div>
                                                {/* Kein truncate mehr: auf dem Handy war der Titel nach
                                                    zwei Woertern abgeschnitten, dabei ist er das Einzige,
                                                    woran der Nutzer die Position erkennt. */}
                                                <h4 className="font-bold text-slate-800 text-sm leading-snug">{s.title}</h4>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                                                    <span className="text-[11px] font-bold text-slate-500 uppercase">Rest: <span className={remaining <= 0 ? 'text-red-500' : 'text-slate-700'}>{remaining} {s.unit}</span></span>
                                                    <span className="text-[11px] font-bold text-slate-300 hidden sm:inline">•</span>
                                                    <span className="text-[11px] font-bold text-slate-500 uppercase">Vertrag: {s.quantityContracted}</span>
                                                </div>
                                                {minHours !== null && (
                                                    <p className="text-[11px] text-blue-700 font-bold mt-2 flex items-center gap-1">
                                                        <Info className="w-3 h-3" /> Mindestens {formatQuantity(minHours)} Std. ansetzen.
                                                    </p>
                                                )}
                                            </div>

                                            {/* Minus | Feld | Plus — auf dem Handy volle Breite mit
                                                grossen Tastflaechen, am Schreibtisch kompakt rechts. */}
                                            <div className="flex items-stretch gap-2 w-full md:w-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => step(s.id, isHourly, -1)}
                                                    disabled={!selected}
                                                    aria-label="Menge verringern"
                                                    className="w-12 md:w-10 shrink-0 rounded-xl border-2 border-slate-200 text-slate-600 flex items-center justify-center active:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <div className="relative flex-1 md:w-32">
                                                    {/*
                                                      * type="text" + inputMode="decimal", NICHT
                                                      * type="number": das Zahlenfeld meldet ein
                                                      * getipptes "1,5" oder "2." je nach
                                                      * Gebietsschema als leeren Wert und liesse
                                                      * eine sichtbare Menge ungebucht.
                                                      * text-base auf dem Handy: unter 16px zoomt
                                                      * iOS beim Fokus in die Seite hinein.
                                                      */}
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder="0"
                                                        value={amountTexts[s.id] ?? ''}
                                                        onChange={(e) => handleValueChange(s.id, e.target.value)}
                                                        className={`w-full h-12 md:h-auto md:p-2.5 pr-12 md:pr-10 border-2 rounded-xl text-center font-bold text-base md:text-sm outline-none transition-all ${selected ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 focus:border-indigo-300'}`}
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400 pointer-events-none">{s.unit}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => step(s.id, isHourly, 1)}
                                                    aria-label="Menge erhöhen"
                                                    className={`w-12 md:w-10 shrink-0 rounded-xl border-2 flex items-center justify-center transition-colors ${selected ? 'border-indigo-500 bg-indigo-600 text-white active:bg-indigo-700' : 'border-indigo-200 bg-indigo-50 text-indigo-600 active:bg-indigo-100'}`}
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {overWarning && (
                    <div role="alert" className="px-4 sm:px-5 py-3 bg-amber-50 border-t border-amber-200 shrink-0 space-y-2">
                        <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" /> Mehr als im Vertrag übrig
                        </p>
                        <ul className="space-y-0.5 text-xs text-amber-900">
                            {overWarning.map(o => (
                                <li key={o.serviceId}>
                                    <span className="font-mono font-bold">{o.lvPosition}</span> {o.title}: {formatQuantity(o.quantity)} {o.unit} eingetragen, Rest {formatQuantity(o.remaining)} {o.unit}
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-amber-800">
                            Nur bestätigen, wenn diese Leistung heute wirklich zusätzlich erbracht wurde.
                        </p>
                        <button
                            type="button"
                            onClick={() => setOverWarning(null)}
                            className="text-xs font-bold text-amber-900 underline underline-offset-2"
                        >
                            Mengen ändern
                        </button>
                    </div>
                )}

                {/* Fuss: auf dem Handy gestapelt — der Hauptknopf oben in voller
                    Breite, darunter die beiden Nebenwege in einer Zeile. Vorher
                    standen alle drei nebeneinander und „Weiter" ragte rechts
                    aus dem Dialog. Am Schreibtisch wie gehabt. */}
                <div className="p-4 sm:p-5 bg-white border-t shrink-0 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-5">
                    {/* Zwei Abbrechen-Knoepfe im Markup, je einer pro Layout,
                        ueber HUELLEN ein-/ausgeblendet — `hidden` direkt auf
                        dem Button verlor gegen dessen eigenes `inline-flex`,
                        beide standen auf dem Handy nebeneinander. */}
                    <div className="flex justify-between items-center sm:contents">
                        <button
                            onClick={() => onConfirm({})}
                            className="text-slate-400 hover:text-slate-600 text-sm font-bold px-2 sm:px-4 py-2"
                        >
                            Keine Zusatzleistungen
                        </button>
                        <div className="sm:hidden"><Button variant="ghost" onClick={onClose}>Abbrechen</Button></div>
                    </div>
                    <div className="flex gap-3 w-full sm:w-auto">
                        <div className="hidden sm:block"><Button variant="ghost" onClick={onClose}>Abbrechen</Button></div>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleConfirm}
                            className={`${overWarning ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-100' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'} w-full sm:w-auto sm:px-8 shadow-lg`}
                            icon={<ArrowRight className="w-4 h-4" />}
                        >
                            {overWarning
                                ? 'Trotzdem buchen'
                                : hasAnySelection ? `${selectedCount === 1 ? 'Menge' : 'Mengen'} übernehmen` : 'Weiter'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
};
